import { addHours, differenceInHours, format, formatISO, getHours, isToday, parseISO, startOfDay } from 'date-fns';
import { FORM_ERROR } from 'final-form';
import { parse } from 'query-string';
import { ERROR_ACCOUNT_BLOCKED, ERROR_CODE_EXPIRED, ERROR_CODE_INCORRECT, ERROR_EMAIL_INVALID, ERROR_LOGIN_INCORRECT, ERROR_LOGIN_ISSUE, ERROR_PASSWORD_EMPTY, ERROR_PASSWORD_WRONG, ERROR_RECAPTCHA_GEN, ERROR_USER_ROLE_INVALID, PAGES_MAX, PAGES_MIN, STORAGE_DEBUG, STORAGE_ORDER, WORDS_MAX, WORDS_MIN, WORDS_TO_NEXT_PAGE_THRESHOLD } from '../constants';
import getDefaultStorage from './getDefaultStorage';
export { buildGtag, default } from "../utils/gtm";
export const composeValidators = (...validators) => value => validators.reduce((error, v) => error || v(value), undefined);
export const getClosestValidDate = date => {
  if (isToday(date)) {
    if (differenceInHours(date, new Date()) < 2) {
      return addHours(startOfDay(new Date()), getHours(new Date()) + 3);
    } else {
      return date;
    }
  } else {
    return addHours(startOfDay(date), 12);
  }
};
export const formatDate = (date, stringFormat = 'MMM dd, yyyy') => {
  if (!date) return '';
  return format(parseISO(date), stringFormat);
};
export const getDefaultDate = () => {
  return addHours(startOfDay(new Date()), getHours(new Date()) + 24);
};
export const convertWordsToPages = (w, wordsPerPage = 275) => {
  const words = parseInt(w, 10);
  if (Number.isNaN(words)) {
    return 1;
  }
  return Math[words % wordsPerPage < WORDS_TO_NEXT_PAGE_THRESHOLD ? 'floor' : 'ceil'](words / wordsPerPage);
};

/**
 * @desc stringify large numbers to display k instead of 1000 for numbers over 9999
 * @param {String|number} num
 * @returns {String}
 */
export const stringifyNumberWithK = num => {
  const parsedNum = parseInt(num, 10);
  if (Number.isNaN(num)) {
    return `${parsedNum}`;
  }
  if (parsedNum > 9999) {
    return `~${Math.floor(parsedNum / 1000)}k`;
  }
  return parsedNum;
};

/**
 * @desc normalize page to be in range between MIN_PAGES and MAX_PAGES
 * @param {String|number} p
 * @returns {number}
 */
export const normalizePages = (p = 1) => {
  const pages = parseInt(p, 10);
  if (Number.isNaN(pages)) {
    return 1;
  }
  return pages <= 0 ? Math.max(pages, PAGES_MIN) : Math.min(pages, PAGES_MAX);
};

/**
 * @desc normalize words to be in positive range starting from MIN_WORDS
 * @param {String|number} w
 * @returns {number}
 */
export const normalizeWords = (w = 1) => {
  const words = parseInt(w, 10);
  if (Number.isNaN(words)) {
    return WORDS_MIN;
  }
  return words < WORDS_MIN ? Math.max(words, WORDS_MIN) : Math.min(words, WORDS_MAX);
};

/**
 * TODO deprecated
 * @deprecated
 * @sideeffect {Function} decodeURIComponent
 * @param {String} search
 * @returns {Object}
 */
export function parseSearchParams(search) {
  return search.slice(search.indexOf('?') + 1).split('&').reduce((acc, hash) => {
    const [key, val] = decodeURIComponent(hash).split(/=(.*)/s, 2);
    return {
      ...acc,
      [key]: decodeURIComponent(val)
    };
  }, {});
}

/**
 * @sideeffect {Function} encodeURIComponent
 * @param {Object} obj
 * @returns {String} searchParams
 */
export function toSearchParams(obj) {
  return Object.keys(obj).filter(key => obj[key]).reduce((searchStr, key, i, arr) => {
    const delimiter = arr.length - 1 === i ? '' : '&';
    return searchStr.concat(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}${delimiter}`);
  }, '?');
}

/**
 * @sideeffect {Function} encodeURIComponent
 * @param {Object} obj
 * @returns {String} searchParams
 */
export function toUrlEncoded(obj) {
  return Object.keys(obj).reduce((searchStr, key, i, arr) => {
    const delimiter = arr.length - 1 === i ? '' : '&';
    return searchStr.concat(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}${delimiter}`);
  }, '');
}

/**
 * @param {Date} date
 * @returns {String} iso
 */
export function formatISOUtc(date = new Date()) {
  const timeISO = formatISO(date);
  return timeISO.includes('Z') ? timeISO : `${timeISO.slice(0, -6)}Z`;
}
export const find = path => obj => path.split('.').reduce((acc, key) => acc[key], obj);
export const is = (...keys) => state => keys.reduce((acc, key) => acc && find(key)(state), true);
export const isNot = (...keys) => state => keys.reduce((acc, key) => acc && !find(key)(state), true);
export const equal = (key, value) => state => is(key)(state) === value;
export const and = (...fns) => state => fns.reduce((acc, fn) => acc && fn(state), true);
export const when = (condition, f) => x => condition(x) ? f(x) : x;
export const arg = (arg, f) => state => f(arg)(state);
export const apipe = (...fns) => state => fns.reduce((acc, fn) => acc.then(fn), Promise.resolve(state));
export const whenSideEffect = (condition, fn) => state => {
  condition(state) && fn(state);
  return state;
};
export const sideEffect = fn => state => {
  fn && fn(state);
  return state;
};

/**
 * XXX Bad/Better pipe (extends function's new state with prev state automatically)
 * @TODO refactoring (replace local variable `prev`)
 */
export const bpipe = (...fns) => state => fns.reduce((acc, fn) => {
  let prev;
  return acc.then(state => {
    prev = state;
    return fn(state);
  }).then(state => ({
    ...prev,
    ...state
  }));
}, Promise.resolve(state));

/**
 * @param {Function} fn
 * @param {String} name
 */
export const debug = (fn, name) => state => {
  if (getDefaultStorage().getItem(STORAGE_DEBUG)) {
    console.group(name);
    console.dir(state);
    console.groupEnd();
  }
  try {
    return fn(state);
  } catch (e) {
    console.error(name, e);
    return state;
  }
};

/**
 * @param {State} state
 * @param {Object} state.errors
 * @returns {State}
 */
export function handleErrors(state) {
  const {
    errors
  } = state;
  const errorsToFields = {
    policy_required: 'policy_agree',
    invalid_email: 'login',
    password_incorrect: 'password',
    user_blocked: 'login',
    auth_token_not_defined: 'password',
    auth_token_not_actual: 'password',
    auth_token_required_user_not_matched: 'login',
    user_role_required_user_not_matched: 'login',
    policy_agreement_not_full: 'policy_agree',
    login_incorrect: 'login',
    loginability_issue: 'password',
    recaptcha_gen_error: 'login',
    code_incorrect: 'code',
    code_expired: 'code'
  };
  const errorsTexts = {
    policy_required: true,
    policy_agreement_not_full: true,
    invalid_email: ERROR_EMAIL_INVALID,
    password_incorrect: ERROR_PASSWORD_WRONG,
    user_blocked: ERROR_ACCOUNT_BLOCKED,
    auth_token_not_defined: ERROR_PASSWORD_EMPTY,
    auth_token_not_actual: ERROR_PASSWORD_EMPTY,
    auth_token_required_user_not_matched: ERROR_USER_ROLE_INVALID,
    user_role_required_user_not_matched: ERROR_USER_ROLE_INVALID,
    login_incorrect: ERROR_LOGIN_INCORRECT,
    loginability_issue: ERROR_LOGIN_ISSUE,
    recaptcha_gen_error: ERROR_RECAPTCHA_GEN,
    code_incorrect: ERROR_CODE_INCORRECT,
    code_expired: ERROR_CODE_EXPIRED
  };
  const formError = Object.keys(errorsToFields).reduce((acc, code) => errors[code] ? code : acc, '');
  const fieldsErrors = Object.keys(errors).reduce((acc, code) => errors[code] ? {
    ...acc,
    [errorsToFields[code]]: errorsTexts[code]
  } : acc, {});
  return formError ? {
    [FORM_ERROR]: formError,
    ...fieldsErrors
  } : undefined;
}

/**
 * @description helper function for form submit loading
 * @param {Boolean} loadingValue
 * @returns function (state) => state
 */
export const formLoading = loadingValue => state => ({
  ...state,
  loading: loadingValue
});

/**
 * @TODO rm, create store with persistent tool
 * @see https://github.com/nanostores/persistent
 * @deprecated
 * @param {String} key
 * @param {'get'|'set'|'rm'} operation
 * @param {*} value
 */
export function storage(key, operation, value) {
  if (!window.sessionStorage) return false;
  const data = JSON.parse(sessionStorage.getItem(key));
  switch (operation) {
    case 'set':
      sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    case 'rm':
      sessionStorage.removeItem(key);
      return true;
    case 'patch':
      sessionStorage.setItem(key, JSON.stringify({
        ...data,
        ...value
      }));
      return true;
    default:
      return JSON.parse(sessionStorage.getItem(key));
  }
}

/**
 * @TODO rm, use stores with persistent tool
 * @see https://github.com/rehooks/local-storage/tree/master
 * @deprecated
 * @param {State} state
 * @returns {State}
 */
export function clearStorage(state) {
  storage(STORAGE_ORDER, 'rm');
  return state;
}

/**
 * @TODO rm, use stores with persistent tool
 * @see https://github.com/nanostores/persistent
 * @deprecated
 * @param {State} state
 * @returns {State}
 */
export function clearStorageAssignment(state) {
  storage(STORAGE_ORDER, 'rm');
  return state;
}

/**
 * @param {String} url
 * @param {24|32|40|64|80|128|256}  size image size
 * @return {String} sized image url
 */
export const getSizedAvatarUrl = (url, size = 128) => {
  try {
    return url.replace('{SIZE}', `${size}`);
  } catch (e) {
    console.error(`Error in getSizedAvatarUrl: ${e}`);
    return url;
  }
};

/**
 * Replace target's ids with dictinary values
 * @param {Object} target
 * @param {Record} dictionaries
 */
export const applyDictionaries = (target, dictionaries) => {
  const slugs = Object.keys(dictionaries);
  return slugs.reduce((acc, slug) => acc[slug] ? {
    ...acc,
    [slug]: dictionaries[slug].find(el => el.id === acc[slug])?.title
  } : acc, target);
};

/**
 * XXX side-effect IntersectionObserver, window
 * @param {Element} el
 * @param {Object} [opts]
 * @param {function} [opts.cb]
 * @param {number} [opts.threshold]
 */
export function scrollTo(el, {
  cb,
  threshold
}) {
  const THRESHOLD = threshold || 80;
  const {
    top
  } = el ? el.getBoundingClientRect() : {
    top: 0
  };
  const observer = new IntersectionObserver(entries => {
    entries.map(entry => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          cb();
        }, 100);
        observer.unobserve(el);
      }
    });
  });
  if (el) {
    observer.observe(el);
  }
  window.scrollTo({
    top: (top + window.scrollY - THRESHOLD).toFixed(),
    behavior: 'smooth'
  });
}

/**
 * returns {String}
 */
export function genId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).substr(2)}`;
}
export function isBrowser() {
  return typeof window !== 'undefined';
}
export function canUseDOM() {
  return !!(typeof window !== 'undefined' && window.document && window.document.createElement);
}

/**
 * @param {State} state
 * @returns {State}
 */

export function getRoute(state = {}) {
  const {
    url
  } = parse(window.location.search);
  return url ? {
    ...state,
    route: url
  } : state;
}
export const dummy = () => {};
export const toBoolean = any => {
  switch (typeof any) {
    case 'boolean':
      return any;
    case 'string':
      return any === 'true';
    case 'number':
      return Boolean(any);
    case 'object':
      if (Array.isArray(any)) {
        return any.length > 0;
      }
      if (any instanceof Object) {
        return Object.keys(any).length > 0;
      } else {
        return Boolean(any);
      }
  }
};

/**
 * @param {string} eventName
 * @param {any} data
 */
export function dispatchCustomEvent(eventName, data = {}) {
  const event = new CustomEvent(eventName, {
    detail: data
  });
  document.dispatchEvent(event);
}

/**
 * @description Get best discipline from disciplines array by orders_qty
 * @param {Array} disciplines
 * @returns {Object} discipline
 */
export const getBestInDiscipline = (disciplines = [{
  orders_qty: 355
}]) => {
  try {
    const sortedDisciplines = disciplines.sort((a, b) => b.orders_qty - a.orders_qty);
    return sortedDisciplines[0];
  } catch (e) {
    console.warn(`Error in getBestInDiscipline: ${e.message} `);
    return disciplines[0];
  }
};